import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { density, radius, statusChipStyle, typography } from "@aeriosiq/ui";
import {
  TriangleAlertIcon,
  UmbrellaIcon,
  WrenchIcon,
} from "@aeriosiq/ui/icons";

import {
  activeAssignment,
  anchorChoices,
  assignmentLabel,
  defaultAnchorFor,
  type AnchorChoice,
} from "./anchor.js";
import {
  fromDateTimeLocalValue,
  formatDurationHm,
  minutesBetween,
  toDateTimeLocalValue,
} from "./format.js";
import { isLockedConflict } from "./timeTrackingFetch.js";
import {
  useCreateTimeTrackingMutation,
  useMyTimeTrackingContextQuery,
  useUpdateTimeTrackingMutation,
} from "./hooks.js";
import {
  STANDBY_TYPES,
  type MyTimeTrackingContextItem,
  type StandbyType,
  type TimeTracking,
} from "./types.js";
import { LOCKED_EXPLANATION } from "./LockStateChip.js";
import {
  OnBehalfAnchorFields,
  type OnBehalfSelection,
} from "./review/OnBehalfAnchorFields.js";

/**
 * TT-02 — report or edit a standby. Mockup screen A.
 *
 * ONE implementation for both viewports: `fullScreen` on mobile turns the
 * dialog into the full page the story asks for, so the field flow and the desk
 * flow can never drift apart.
 *
 * TOUCH DENSITY, NOT COMPACT. Every control is `density.comfortable`
 * (44px — WCAG 2.5.5 AAA) because this is the one page a pilot uses outdoors
 * with gloves on. D77's compact preset is explicitly barred from touch
 * surfaces; the desktop rendering inherits the same size rather than shrinking,
 * which costs a desk user nothing.
 *
 * The invoice warning is carried over verbatim from AERO Inspection 1 — it
 * worked there, and wording mistakes on invoices are expensive.
 */
export const INVOICE_WARNING =
  "This text appears 1:1 on the invoice to the end customer — check the wording.";

export interface TimeTrackingFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that tracking; absent → create a new one. */
  editing?: TimeTracking | null;
  /** Full-screen (mobile) rendering. */
  fullScreen?: boolean;
  onSaved?: () => void;
  /**
   * TT-04 — back-office mode. When set, the form books time FOR somebody else
   * inside this project: the anchor comes from the project's own parks and
   * turbines instead of the caller's `my-context` (a back-office user has no
   * assignments, so that path dead-ends), and a pilot picker supplies `userId`.
   *
   * Ignored while editing — an existing row already knows whose time it is.
   */
  onBehalfOfProject?: { projectId: string; projectLabel: string | null } | null;
}

interface FormState {
  activityType: StandbyType;
  anchor: AnchorChoice | null;
  /** `datetime-local` values (local wall clock), converted on submit. */
  start: string;
  end: string;
  description: string;
}

/** Now, rounded DOWN to the minute — a standby starts when it starts. */
function nowLocal(): string {
  return toDateTimeLocalValue(new Date().toISOString());
}

export function TimeTrackingFormDialog({
  open,
  onClose,
  editing = null,
  fullScreen = false,
  onSaved,
  onBehalfOfProject = null,
}: TimeTrackingFormDialogProps) {
  const theme = useTheme();
  const isEdit = editing != null;
  // Back-office create. Editing always uses the normal path — the row's anchor
  // and its owner are already decided.
  const onBehalf = !isEdit && onBehalfOfProject ? onBehalfOfProject : null;

  const contextQuery = useMyTimeTrackingContextQuery({
    // Don't ask for the caller's assignments in back-office mode: they have
    // none, and the empty answer would render the wrong empty state.
    enabled: open && onBehalf == null,
  });
  const assignments = contextQuery.data?.assignments;

  // The picker branch: no active assignment, or the pilot said "working
  // somewhere else". Index into `assignments` rather than an id so the
  // "choose another" affordance and the default share one source of truth.
  const [pickedAssignmentId, setPickedAssignmentId] = useState<string | null>(
    null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const assignment = useMemo(() => {
    if (pickedAssignmentId) {
      return (
        assignments?.find(
          (a) => a.inspectionInstructionId === pickedAssignmentId,
        ) ?? null
      );
    }
    return activeAssignment(assignments);
  }, [assignments, pickedAssignmentId]);

  const choices = useMemo(() => anchorChoices(assignment), [assignment]);

  const [form, setForm] = useState<FormState>(() => ({
    activityType: "standby_weather",
    anchor: null,
    start: nowLocal(),
    end: "",
    description: "",
  }));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lockedConflict, setLockedConflict] = useState(false);
  /** Back-office mode only — whose time this is. */
  const [pilotUserId, setPilotUserId] = useState<string | null>(null);

  // Stable identity: `OnBehalfAnchorFields` reports through an effect, so an
  // inline callback would re-fire it on every render.
  const handleOnBehalfChange = useCallback((sel: OnBehalfSelection) => {
    setPilotUserId(sel.pilotUserId);
    setForm((f) =>
      f.anchor === sel.anchor ? f : { ...f, anchor: sel.anchor },
    );
  }, []);

  // Reset whenever the dialog opens — a stale draft from a previous open is a
  // worse default than an empty form.
  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setLockedConflict(false);
    setPickerOpen(false);
    setPickedAssignmentId(null);
    if (editing) {
      setForm({
        activityType:
          editing.activityType === "standby_technical"
            ? "standby_technical"
            : "standby_weather",
        anchor: {
          // On edit the anchor is whatever the row already carries; the chips
          // below re-derive from the assignment and may not contain it, which
          // is correct — an edited row keeps its anchor unless changed.
          level:
            editing.entityType === "contract_position"
              ? "turbine"
              : editing.entityType === "contract"
                ? "park"
                : "project",
          entityType: editing.entityType,
          entityId: editing.entityId,
          label: editing.anchorLabel,
        },
        start: toDateTimeLocalValue(editing.startTime),
        end: toDateTimeLocalValue(editing.endTime),
        description: editing.description ?? "",
      });
    } else {
      setForm({
        activityType: "standby_weather",
        anchor: null,
        start: nowLocal(),
        end: "",
        description: "",
      });
    }
  }, [open, editing]);

  // Pre-select the anchor the standby type implies, once the chips exist.
  // Weather → the whole park; technical → this turbine (anchor.ts explains why).
  useEffect(() => {
    if (!open || isEdit || onBehalf) return;
    if (choices.length === 0) return;
    setForm((f) =>
      f.anchor && choices.some((c) => c.entityId === f.anchor?.entityId)
        ? f
        : { ...f, anchor: defaultAnchorFor(f.activityType, choices) },
    );
  }, [open, isEdit, onBehalf, choices]);

  const createMutation = useCreateTimeTrackingMutation();
  const updateMutation = useUpdateTimeTrackingMutation();
  const saving = createMutation.isPending || updateMutation.isPending;

  const durationMinutes = minutesBetween(
    fromDateTimeLocalValue(form.start),
    fromDateTimeLocalValue(form.end),
  );

  const validation =
    validate(form, durationMinutes) ??
    (onBehalf && !pilotUserId ? "Pick the pilot this time belongs to." : null);
  const canSubmit = validation == null && !saving;

  function pickType(activityType: StandbyType): void {
    setForm((f) => ({
      ...f,
      activityType,
      // Re-derive the default anchor on a type switch, but never overwrite an
      // anchor the pilot chose by hand (that is the "manual override always
      // possible" rule).
      anchor:
        isEdit || onBehalf || !choices.length
          ? f.anchor
          : (defaultAnchorFor(activityType, choices) ?? f.anchor),
    }));
  }

  async function handleSubmit(): Promise<void> {
    setSubmitError(null);
    setLockedConflict(false);
    const startIso = fromDateTimeLocalValue(form.start);
    const endIso = fromDateTimeLocalValue(form.end);
    if (!startIso || !endIso || !form.anchor) return;
    if (onBehalf && !pilotUserId) return;
    try {
      if (editing) {
        await updateMutation.mutateAsync({
          id: editing.timeTrackingId,
          body: {
            entityType: form.anchor.entityType,
            entityId: form.anchor.entityId,
            activityType: form.activityType,
            startTime: startIso,
            endTime: endIso,
            description: form.description,
          },
        });
      } else {
        await createMutation.mutateAsync({
          entityType: form.anchor.entityType,
          entityId: form.anchor.entityId,
          activityType: form.activityType,
          startTime: startIso,
          endTime: endIso,
          description: form.description,
          // Retro-booking (Q-TT-1). Omitted outside back-office mode, so the
          // BE attributes the time to the caller.
          ...(onBehalf && pilotUserId ? { userId: pilotUserId } : {}),
        });
      }
      onSaved?.();
      onClose();
    } catch (error) {
      // A 409 `locked` is not a failure to shout about — the back office
      // raised an invoice while this row was on screen. Explain and let the
      // refetch (onSettled) replace the stale copy.
      if (isLockedConflict(error)) {
        setLockedConflict(true);
        return;
      }
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Could not save the time tracking.",
      );
    }
  }

  const labelSx = {
    ...typographyLabelTier,
    color: "text.secondary",
    mt: 2,
    mb: 1,
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="time-tracking-form-title"
    >
      <DialogTitle id="time-tracking-form-title">
        {isEdit
          ? "Edit time tracking"
          : onBehalf
            ? "Book standby for a pilot"
            : "New time tracking"}
      </DialogTitle>
      <DialogContent dividers>
        {lockedConflict ? (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="tt-locked-notice">
            {LOCKED_EXPLANATION}
          </Alert>
        ) : null}
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        {/* ── Type ─────────────────────────────────────────────────────── */}
        <Typography component="p" sx={labelSx}>
          Type
        </Typography>
        <Box
          role="radiogroup"
          aria-label="Standby type"
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          {STANDBY_TYPES.map((t) => {
            const selected = form.activityType === t;
            const chip = statusChipStyle(theme, "info");
            const Glyph = t === "standby_technical" ? WrenchIcon : UmbrellaIcon;
            return (
              <Box
                key={t}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  pickType(t);
                }}
                data-testid={`tt-type-${t}`}
                sx={{
                  // Comfortable density ×2 — a gloved thumb target, and the
                  // glyph needs a line of its own above the label.
                  minHeight: density.comfortable.controlHeight * 2,
                  borderRadius: radius.control,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  px: 1,
                  py: 1.5,
                  font: "inherit",
                  fontWeight: 600,
                  backgroundColor: selected
                    ? chip.backgroundColor
                    : "transparent",
                  color: selected ? "text.primary" : "text.secondary",
                  // A SELECTION edge, not a status keyline (D91 retired
                  // those): the hue itself marks the chosen card.
                  border: `1px solid ${
                    selected ? chip.dotColor : theme.palette.divider
                  }`,
                }}
              >
                <Glyph
                  fontSize="medium"
                  sx={{ color: selected ? chip.dotColor : "inherit" }}
                />
                {t === "standby_weather"
                  ? "Weather standby"
                  : "Technical standby"}
              </Box>
            );
          })}
        </Box>

        {/* ── Anchor ───────────────────────────────────────────────────── */}
        <Typography component="p" sx={labelSx}>
          {onBehalf ? "Pilot and anchor" : "Applies to"}
        </Typography>
        {onBehalf ? (
          <OnBehalfAnchorFields
            projectId={onBehalf.projectId}
            projectLabel={onBehalf.projectLabel}
            activityType={form.activityType}
            onChange={handleOnBehalfChange}
            disabled={saving}
          />
        ) : contextQuery.isPending ? (
          <Typography variant="body2" color="text.secondary">
            Loading your assignments…
          </Typography>
        ) : choices.length === 0 || pickerOpen ? (
          <AssignmentPicker
            assignments={assignments ?? []}
            value={
              pickedAssignmentId ?? assignment?.inspectionInstructionId ?? ""
            }
            onChange={(id) => {
              setPickedAssignmentId(id);
              setPickerOpen(false);
            }}
          />
        ) : (
          <>
            <Box
              role="radiogroup"
              aria-label="What this standby applies to"
              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
            >
              {choices.map((c) => {
                const selected = form.anchor?.entityId === c.entityId;
                return (
                  <Chip
                    key={`${c.entityType}:${c.entityId}`}
                    label={c.label}
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setForm((f) => ({ ...f, anchor: c }));
                    }}
                    variant={selected ? "filled" : "outlined"}
                    color={selected ? "primary" : "default"}
                    data-testid={`tt-anchor-${c.level}`}
                    sx={{
                      // ≥44px touch target — the chip default is 32.
                      height: density.comfortable.controlHeight,
                      borderRadius: radius.pill,
                      fontWeight: 600,
                    }}
                  />
                );
              })}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Working somewhere else?{" "}
              <Link
                component="button"
                type="button"
                onClick={() => {
                  setPickerOpen(true);
                }}
              >
                Choose another assignment
              </Link>
            </Typography>
          </>
        )}

        {/* ── Time ─────────────────────────────────────────────────────── */}
        <Typography component="p" sx={labelSx}>
          Time
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            label="Start"
            type="datetime-local"
            value={form.start}
            onChange={(e) => {
              setForm((f) => ({ ...f, start: e.target.value }));
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-start" },
            }}
            fullWidth
          />
          <TextField
            label="End"
            type="datetime-local"
            value={form.end}
            onChange={(e) => {
              setForm((f) => ({ ...f, end: e.target.value }));
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-end" },
            }}
            fullWidth
          />
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
          data-testid="tt-duration"
        >
          {durationMinutes != null && durationMinutes > 0
            ? `Duration ${formatDurationHm(durationMinutes)}`
            : "Duration —"}
        </Typography>

        {/* ── Description ──────────────────────────────────────────────── */}
        <Typography component="p" sx={labelSx}>
          Description — printed on the customer invoice
        </Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={form.description}
          onChange={(e) => {
            setForm((f) => ({ ...f, description: e.target.value }));
          }}
          placeholder="What happened, in the words the customer will read."
          slotProps={{
            // `aria-describedby` belongs on the TEXTAREA, not on the
            // FormControl wrapper — a screen reader announcing the field has
            // to reach the invoice warning, which is the whole point of it.
            htmlInput: {
              maxLength: 4000,
              "data-testid": "tt-description",
              "aria-describedby": "tt-invoice-warning",
            },
          }}
        />
        <Alert
          id="tt-invoice-warning"
          severity="warning"
          icon={<TriangleAlertIcon fontSize="small" />}
          sx={{ mt: 1 }}
        >
          {INVOICE_WARNING}
        </Alert>

        {validation ? (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 2 }}
            data-testid="tt-validation"
          >
            {validation}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ minHeight: density.comfortable.controlHeight }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            void handleSubmit();
          }}
          data-testid="tt-save"
          sx={{ minHeight: density.comfortable.controlHeight }}
        >
          {isEdit ? "Save changes" : "Save time tracking"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The no-active-task branch — a picker over the same my-context list. */
function AssignmentPicker({
  assignments,
  value,
  onChange,
}: {
  assignments: readonly MyTimeTrackingContextItem[];
  value: string;
  onChange: (id: string) => void;
}) {
  if (assignments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        You have no open assignments, so there is nothing to anchor a standby to
        yet. Ask your project manager to assign you an inspection instruction.
      </Typography>
    );
  }
  return (
    <TextField
      select
      fullWidth
      label="Assignment"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      slotProps={{
        inputLabel: { shrink: true },
        htmlInput: { "data-testid": "tt-assignment-picker" },
      }}
    >
      {assignments.map((a) => (
        <MenuItem
          key={a.inspectionInstructionId}
          value={a.inspectionInstructionId}
          sx={{ minHeight: density.comfortable.controlHeight }}
        >
          {assignmentLabel(a)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * The label tier — 11px IBM Plex Mono caps at +0.14em, taken as ONE object from
 * the token layer (D77) rather than reassembled from four separate tokens at
 * the call site.
 */
const typographyLabelTier = typography.label;

/**
 * The one place the form's rules live. Returns the message to show, or `null`
 * when the form is submittable. Exported for the component tier.
 */
export function validate(
  form: {
    anchor: AnchorChoice | null;
    start: string;
    end: string;
  },
  durationMinutes: number | null,
): string | null {
  if (!form.anchor) return "Pick what this standby applies to.";
  if (!form.start) return "Enter a start time.";
  if (!form.end) return "Enter an end time.";
  if (durationMinutes == null) return "Enter a valid start and end time.";
  if (durationMinutes <= 0) return "The end time must be after the start time.";
  return null;
}
